import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Use localhost for development, production URL for deployment
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE_URL = isDevelopment ? 'http://localhost:8000/api' : 'https://trigabantin.medupdates.in/api';
const BACKEND_URL = API_BASE_URL.replace('/api', '');

const PreviewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    doctor, 
    previewUrl, posterUrl,  // Legacy/primary
    previewUrlV1, posterUrlV1,  // V1 Star Map
    previewUrlV6, posterUrlV6   // V6 Cosmic Profile
  } = location.state || {};
  const [doctorDetails, setDoctorDetails] = useState(doctor || null);
  
  // Tab state - default to V6 (Cosmic Profile)
  const [activeTab, setActiveTab] = useState('v6');
  
  // Image blob URLs for displaying (fetched via backend proxy to avoid CORS)
  const [imageUrlV1, setImageUrlV1] = useState(null);
  const [imageUrlV6, setImageUrlV6] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // Always refresh doctor details from API so DOB/city/time reflect latest edits.
  useEffect(() => {
    const fetchLatestDoctor = async () => {
      if (!doctor?.id) return;
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/doctors/`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
        });
        if (!response.ok) return;

        const doctors = await response.json();
        const latest = Array.isArray(doctors)
          ? doctors.find((d) => d.id === doctor.id)
          : null;
        if (latest) {
          setDoctorDetails(latest);
        }
      } catch (err) {
        console.log('Failed to refresh latest doctor details');
      }
    };

    fetchLatestDoctor();
  }, [doctor?.id]);
  
  // Fetch preview images via backend proxy to avoid CORS issues
  // Falls back to download endpoint if preview fails (since downloads always work)
  useEffect(() => {
    let isMounted = true;
    
    const fetchImageWithFallback = async (version) => {
      const token = localStorage.getItem('token');
      if (!token || !doctorDetails?.id) return null;
      
      // Retry up to 3 times with delay between attempts
      for (let attempt = 1; attempt <= 3; attempt++) {
        // Add cache-buster to prevent browser from returning cached responses
        const cacheBuster = `&_t=${Date.now()}`;
        
        // Try preview endpoint first
        try {
          const response = await fetch(`${API_BASE_URL}/doctors/${doctorDetails.id}/preview-image?version=${version}${cacheBuster}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
          });
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              return URL.createObjectURL(blob);
            }
          }
        } catch (err) {
          console.log(`Preview endpoint attempt ${attempt} failed for ${version}`);
        }
        
        // Fallback: Use download endpoint (same file, always works)
        try {
          const response = await fetch(`${API_BASE_URL}/doctors/${doctorDetails.id}/download?version=${version}${cacheBuster}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
          });
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              console.log(`Using download endpoint for ${version} preview`);
              return URL.createObjectURL(blob);
            }
          }
        } catch (err) {
          console.log(`Download fallback attempt ${attempt} failed for ${version}`);
        }
        
        // Wait before retry (except on last attempt)
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      }
      
      return null;
    };
    
    const fetchPreviewImages = async () => {
      if (!doctorDetails?.id) {
        setImageLoading(false);
        return;
      }
      
      setImageLoading(true);
      
      // Fetch both versions - always try if doctor.id exists (fallback uses download endpoint)
      const [v1Url, v6Url] = await Promise.all([
        fetchImageWithFallback('v1'),
        fetchImageWithFallback('v6')
      ]);
      
      if (isMounted) {
        if (v1Url) setImageUrlV1(v1Url);
        if (v6Url) setImageUrlV6(v6Url);
        setImageLoading(false);
      }
    };
    
    fetchPreviewImages();
    
    // Cleanup
    return () => {
      isMounted = false;
      if (imageUrlV1) URL.revokeObjectURL(imageUrlV1);
      if (imageUrlV6) URL.revokeObjectURL(imageUrlV6);
    };
  }, [doctorDetails?.id, retryCount]);
  
  // Retry loading previews
  const handleRetry = () => {
    setImageUrlV1(null);
    setImageUrlV6(null);
    setRetryCount(prev => prev + 1);
  };
  
  // Get current image based on active tab
  const currentImageUrl = activeTab === 'v1' ? imageUrlV1 : imageUrlV6;
  
  // Check if we have both versions (based on successfully fetched images)
  const hasBothVersions = imageUrlV1 && imageUrlV6;

  // Download state for showing spinner on button
  const [downloading, setDownloading] = useState(false);
  
  const handleDownload = async (version = activeTab) => {
    if (doctorDetails?.id) {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to download');
        return;
      }
      
      const filename = version === 'v1' 
        ? `star_map_dr_${doctorDetails.name?.replace(/\s+/g, '_') || 'doctor'}.jpg`
        : `cosmic_profile_dr_${doctorDetails.name?.replace(/\s+/g, '_') || 'doctor'}.jpg`;
      
      setDownloading(true);
      
      // Silent retry - try up to 5 times with 2 second delay
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          console.log(`Download attempt ${attempt} for ${version}...`);
          
          const response = await fetch(`${API_BASE_URL}/doctors/${doctorDetails.id}/download?version=${version}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              console.log('Download successful');
              setDownloading(false);
              return;
            }
          }
        } catch (error) {
          console.log(`Download attempt ${attempt} failed:`, error.message);
        }
        
        // Wait before retry
        if (attempt < 5) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      setDownloading(false);
      // After all retries failed, show a brief message
      alert('Download taking longer than expected. Please try again.');
    }
  };

  const handleCreateNew = () => {
    navigate('/form');
  };

  const handleHome = () => {
    navigate('/welcome');
  };

  // If no data, redirect to form
  const hasAnyPreview = previewUrlV1 || previewUrlV6 || previewUrl;
  if (!doctorDetails || !hasAnyPreview) {
    return (
      <div className="container" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#666', marginBottom: '20px' }}>No preview available</p>
          <button className="btn btn-primary" onClick={() => navigate('/form')}>
            Create New Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Cosmic Profiles Ready! 🎉</h1>
        <p className="page-subtitle">Dr. {doctorDetails.name}'s personalized astrological frames</p>
      </div>

      {/* Tab Navigation - only show if we have both versions */}
      {hasBothVersions && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
          gap: '8px'
        }}>
          <button
            onClick={() => setActiveTab('v6')}
            style={{
              padding: '10px 20px',
              borderRadius: '20px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              backgroundColor: activeTab === 'v6' ? '#667eea' : '#e0e0e0',
              color: activeTab === 'v6' ? '#fff' : '#333',
              transition: 'all 0.2s ease'
            }}
          >
            🌟 Cosmic Profile
          </button>
          <button
            onClick={() => setActiveTab('v1')}
            style={{
              padding: '10px 20px',
              borderRadius: '20px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              backgroundColor: activeTab === 'v1' ? '#667eea' : '#e0e0e0',
              color: activeTab === 'v1' ? '#fff' : '#333',
              transition: 'all 0.2s ease'
            }}
          >
            ⭐ Star Map
          </button>
        </div>
      )}

      {/* Preview Image */}
      <div className="preview-container">
        {imageLoading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '300px',
            color: '#666'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e0e0e0',
              borderTop: '3px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '12px'
            }}></div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            Loading preview...
          </div>
        ) : !currentImageUrl ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '300px',
            color: '#666',
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
              {!doctorDetails?.id ? 'No doctor data provided' : 'Preview not available'}
            </div>
            <div style={{ fontSize: '14px', color: '#999' }}>
              {!doctorDetails?.id 
                ? 'Please go back and select a doctor to view' 
                : 'The poster may not have been generated yet'}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              {doctorDetails?.id && (
                <button 
                  onClick={handleRetry}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#17a2b8',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  🔄 Retry
                </button>
              )}
              <button 
                onClick={() => navigate('/doctors')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Go to Doctor List
              </button>
            </div>
          </div>
        ) : (
          <img
            src={currentImageUrl}
            alt={`${activeTab === 'v1' ? 'Star Map' : 'Cosmic Profile'} for Dr. ${doctorDetails.name}`}
            className="preview-image"
            style={{
              width: '100%',
              maxWidth: '600px',
              objectFit: 'contain'
            }}
          />
        )}
      </div>

      {/* Doctor Info */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ 
          fontSize: '16px', 
          fontWeight: '600', 
          marginBottom: '16px',
          color: '#333'
        }}>
          Profile Details
        </h3>
        <div style={{ fontSize: '14px', color: '#555' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span>Name:</span>
            <span style={{ fontWeight: '500' }}>Dr. {doctorDetails.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span>Date of Birth:</span>
            <span style={{ fontWeight: '500' }}>{doctorDetails.dob}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span>Place of Birth:</span>
            <span style={{ fontWeight: '500' }}>{doctorDetails.birth_city}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Time of Birth:</span>
            <span style={{ fontWeight: '500' }}>{doctorDetails.time_unknown ? 'Unknown' : (doctorDetails.birth_time || '12:00')}</span>
          </div>
        </div>
      </div>

      {/* MR Info */}
      {user && (
        <div className="card" style={{ marginBottom: '24px', backgroundColor: '#f0f7ff' }}>
          <div style={{ fontSize: '14px', color: '#555' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Created by:</span>
              <span style={{ fontWeight: '500' }}>{user.mr_name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Employee Code:</span>
              <span style={{ fontWeight: '500' }}>{user.mr_emp_code}</span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ marginTop: 'auto' }}>
        {/* 
          ===========================================
          DOWNLOAD BUTTONS - HIDDEN (uncomment to enable)
          ===========================================
        */}
        {/* Download current poster
        <button
          onClick={() => handleDownload(activeTab)}
          className="btn btn-primary"
          style={{ marginBottom: '12px' }}
          disabled={downloading}
        >
          {downloading ? '⏳ Downloading...' : `📥 Download ${activeTab === 'v1' ? 'Star Map' : 'Cosmic Profile'}`}
        </button>
        */}

        {/* Download both - only show if both versions exist
        {hasBothVersions && (
          <button
            onClick={async () => {
              await handleDownload('v6');
              setTimeout(() => handleDownload('v1'), 500);
            }}
            className="btn btn-secondary"
            style={{ marginBottom: '12px', backgroundColor: '#f0f7ff', borderColor: '#667eea', color: '#667eea' }}
            disabled={downloading}
          >
            {downloading ? '⏳ Downloading...' : '📥 Download Both Posters'}
          </button>
        )}
        */}

        <button
          onClick={handleCreateNew}
          className="btn btn-secondary"
          style={{ marginBottom: '12px' }}
        >
          ✨ Create Another Profile
        </button>

        <button
          onClick={handleHome}
          className="btn btn-secondary"
        >
          🏠 Back to Home
        </button>
      </div>

      {/* Footer note - HIDDEN (uncomment when downloads are enabled)
      <p style={{ 
        textAlign: 'center', 
        marginTop: '24px', 
        fontSize: '12px', 
        color: '#999' 
      }}>
        {hasBothVersions 
          ? 'Both posters are available in full resolution.' 
          : 'The full resolution image will be downloaded.'}
      </p>
      */}
    </div>
  );
};

export default PreviewPage;
